// src/App.jsx
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import Banner from "./components/Banner";
import Trailers from "./pages/Trailers";
import AuthModal from "./components/AuthModal";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";
import Profile from "./pages/Profile";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Footer from "./components/Footer";
import Blogs from "./pages/Blogs";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Community
import CommunityLayout from "./pages/Community";
import GeneralDiscussion from "./pages/Community/GeneralDiscussion";
import MissionsIndex from "./pages/Community/MissionsIndex";
import MissionDetail from "./pages/Community/Missions/MissionDetail";
import StoryCharactersIndex from "./pages/Community/StoryCharactersIndex";
import StoryCharacterDetail from "./pages/Community/StoryCharacters/CharacterDetail";
import LocationIndex from "./pages/Community/LocationIndex";
import Location from "./pages/Community/Location/LocationDetail";
import WeponsVehicleIndex from "./pages/Community/WeponsVehicleIndex";
import VehiclesDetails from "./pages/Community/Vehicles/VehicleDetail";
import WeaponsDetails from "./pages/Community/Weapons/WeaponDetail";
import TipsTricksIndex from "./pages/Community/TipsTricksIndex";
import TipsTricks from "./pages/Community/TipsTricks/TipTrickDetail";
import MusicRadioIndex from "./pages/Community/MusicRadioIndex";
import MusicRadioDetail from "./pages/Community/MusicRadio/MusicRadioDetail";
import Achievements from "./pages/Community/Achievements";
import News_Updates from "./pages/Community/News_UpdatesIndex";
import Article from "./pages/Community/News_Updates/Article";
// ---------- Tracking Hook ----------
function usePageTracking() {
  const location = useLocation();
  const [analyticsReady, setAnalyticsReady] = useState(null);

  useEffect(() => {
    const waitForAnalytics = async () => {
      const { isSupported, getAnalytics } = await import("firebase/analytics");
      const { app } = await import("./firebase");

      if (await isSupported()) {
        const analyticsInstance = getAnalytics(app);
        setAnalyticsReady(analyticsInstance);
      }
    };
    waitForAnalytics();
  }, []);

  useEffect(() => {
    if (analyticsReady) {
      import("firebase/analytics").then(({ logEvent }) => {
        logEvent(analyticsReady, "page_view", {
          page_path: location.pathname,
        });
      });
    }
  }, [location, analyticsReady]);
}

// ---------- AppContent ----------
function AppContent({ user, setShowAuthModal, showAuthModal }) {
  usePageTracking();

  return (
    <>
      <Navbar user={user} setShowAuthModal={setShowAuthModal} />
      <ToastContainer position="top-center" autoClose={3000} />

      <Routes>
        {/* Home */}
        <Route
          path="/"
          element={
            <>
              <Banner />
              {/* Below banner: side menu + General Discussion by default (embedded mode) */}
              <section id="community-section">
                <CommunityLayout
                  embedded
                  defaultKey="general"
                  user={user}
                  setShowAuthModal={setShowAuthModal}
                />
              </section>
            </>
          }
        />

        {/* Trailers */}
        <Route path="/trailers" element={<Trailers />} />

        {/* Community (full page: left menu + nested routes/Outlet) */}
        <Route
            path="/community/*"
            element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
          >
            <Route
              index
              element={<GeneralDiscussion user={user} setShowAuthModal={setShowAuthModal} />}
            />
            <Route path="missions" element={<MissionsIndex user={user} />} />
            <Route path="missions/:id" element={<MissionDetail user={user} />} />
            <Route path="story-characters" element={<StoryCharactersIndex />} />
            <Route path="story-characters/:id" element={<StoryCharacterDetail />} />
            <Route path="map_locations" element={<LocationIndex />} />
            <Route path="map_locations/:id" element={<Location />} />
            <Route path="vehicles_weapons" element={<WeponsVehicleIndex />} />
            <Route path="vehicles/:id" element={<VehiclesDetails />} />
            <Route path="weapons/:id" element={<WeaponsDetails />} />
            <Route path="tips_tricks" element={<TipsTricksIndex />} />
            <Route path="tips_tricks/:id" element={<TipsTricks />} />
            <Route path="music_radio" element={<MusicRadioIndex />} />
            <Route path="music_radio/:slugOrId" element={<MusicRadioDetail />} />
            <Route path="achievements" element={<Achievements />} />
            <Route path="news_updates" element={<News_Updates />} />
            <Route path="news_updates/:id" element={<News_Updates />} />
            <Route path="news_updates/article/:id" element={<Article />} />
          </Route>



        {/* Profile */}
        <Route
          path="/profile"
          element={user ? <Profile user={user} /> : <Navigate to="/" replace />}
        />

        {/* Static pages */}
        <Route path="/about-us" element={<About />} />
        <Route path="/contact" element={<Contact user={user} />} />
        <Route path="/terms-and-conditions" element={<Terms />} />
        <Route path="/privacy-policy" element={<Privacy />} />
        <Route path="/blogs" element={<Blogs />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
}

// ---------- Main App ----------
export default function App() {
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Track auth and merge with public profile in Firestore
  useEffect(() => {
    let profileUnsub = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (profileUnsub) {
        profileUnsub();
        profileUnsub = null;
      }

      if (firebaseUser) {
        try {
          const publicRef = doc(db, "public_profiles", firebaseUser.uid);
          profileUnsub = onSnapshot(
            publicRef,
            (snap) => {
              const profile = snap.exists() ? snap.data() : {};
              setUser({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName:
                  profile.displayName ?? firebaseUser.displayName ?? "",
                photoURL: profile.photoURL ?? firebaseUser.photoURL ?? "",
                ...profile,
              });
            },
            (err) => {
              console.error("public_profiles listener error:", err);
              setUser({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName ?? "",
                photoURL: firebaseUser.photoURL ?? "",
              });
            }
          );
        } catch (err) {
          console.error("Error setting public profile listener:", err);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName ?? "",
            photoURL: firebaseUser.photoURL ?? "",
          });
        }
      } else {
        setUser(null);
      }
    });

    return () => {
      unsubscribe();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  return (
    <Router>
      {/* give the top-level container a class so CSS can make it full-height flex column */}
      <div className="app-root" style={{ fontFamily: "sans-serif" }}>
        {/* main area that should expand */}
        <main className="app-main">
          <AppContent
            user={user}
            setShowAuthModal={setShowAuthModal}
            showAuthModal={showAuthModal}
          />
        </main>

        {/* footer stays after main and will be pushed to bottom when main grows */}
        <Footer />
      </div>
    </Router>
  );
}
